/**
 * Downhole invoicing (fieldiq-api#910; #927 the ticket gate): generate an
 * invoice from runs.
 *
 * WHAT CAN BE BILLED (api#927). A run that is closed, or recorded as
 * mobilized-not-run, is not on a live invoice (`invoiced: false`, api#913 — a
 * voided invoice frees its runs), AND whose field ticket the customer has
 * approved (`ticket_status: 'customer_approved'`). Anything else is a 409
 * RUN_NOT_CUSTOMER_APPROVED. So every run that is otherwise billable is listed
 * with its ticket's state, only an approved one can be picked, and the rest
 * link to where they move next — their ticket, or the assembly's page to make
 * one. One invoice is one customer and one province/state (the API refuses a
 * mix), so the dialog picks a customer first and flags a mixed jurisdiction.
 *
 * PRICES ARE THE SIGNED SNAPSHOT, NOT TODAY'S RATE CARD (api#927). The invoice
 * bills each run at the prices its ticket was approved at; today's rate card
 * is only compared, and any difference is written into the invoice's notes.
 * So nothing here prices a run from the rate card any more, and a tool with no
 * rate today does not block a bill the customer has already approved. The
 * money is the API's, on the invoice page once it exists.
 *
 * A refusal is shown as the API wrote it; RUN_NOT_CUSTOMER_APPROVED's `runs[]`
 * is listed with a link to each ticket.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../api/client';
import { useDownholeRuns, useDownholeTickets, useGenerateInvoice } from '../../hooks/useDownhole';
import { formatInstantDay } from '../../lib/calendar-date';
import { DH_BILLING_TYPE_LABEL, dhLabelOrText } from '../../lib/downhole-labels';
import { onlineOrSay } from '../../lib/downhole-offline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import type { DhRun } from '../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhField, useOpenCount } from './DownholeBits';
import { TicketStatusBadge, apiErrorCode } from './ticketBits';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Done and not billed: closed or not-run, not on a live invoice. Unknown
 * billing state is not billable. Whether it may be billed NOW also needs its
 * ticket approved — `isInvoiceable`.
 */
export const isBillable = (r: DhRun) => (r.run_state === 'closed' || r.run_state === 'not_run') && r.invoiced === false;

/** Billable AND the customer has approved its ticket — the only runs the API will invoice (api#927). */
export const isInvoiceable = (r: DhRun) => isBillable(r) && r.ticket_status === 'customer_approved';

/** What stands between a billable run and an invoice, in a few words, and where to go about it. */
export function ticketGate(r: DhRun): { text: string; to: string; link: string } | null {
  if (r.ticket_status === 'customer_approved') return null;
  if (r.dh_ticket_id) {
    const what =
      r.ticket_status === 'draft'
        ? 'its ticket is a draft — the office approves it first'
        : r.ticket_status === 'office_approved'
          ? 'waiting for the customer to approve its ticket'
          : r.ticket_status === 'customer_rejected'
            ? 'the customer rejected its ticket'
            : 'its ticket is not approved';
    return { text: what, to: `/downhole/tickets/${r.dh_ticket_id}`, link: r.ticket_number || 'Open ticket' };
  }
  return { text: 'needs a field ticket before it can be billed', to: `/downhole/assemblies/${r.assembly_id}/build`, link: 'Make one on its assembly' };
}

interface Refusal {
  message: string;
  runs: { run_id: string; run_number: number | null; ticket_number: string | null; ticket_status: string | null }[];
}

export function GenerateInvoiceDialog({
  open,
  onClose,
  tz,
  presetRunIds = [],
  title = 'New invoice from runs',
}: {
  open: boolean;
  onClose: () => void;
  tz: string | null;
  /** A rebill: the runs of the voided invoice, preselected (the approved ones). */
  presetRunIds?: string[];
  title?: string;
}) {
  const navigate = useNavigate();
  const runsQuery = useDownholeRuns();
  // The approved tickets' currencies, for the one-currency check; the run read
  // does not carry its ticket's currency.
  const ticketsQuery = useDownholeTickets({ status: 'customer_approved' }, open);
  const generate = useGenerateInvoice();
  const runsFailure = queryLoadFailure(runsQuery);
  const billable = useMemo(() => (runsQuery.data?.data ?? []).filter(isBillable), [runsQuery.data]);
  const customers = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of billable) if (r.customer_id) m.set(r.customer_id, r.customer_name || 'Unnamed customer');
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [billable]);
  const noCustomer = billable.filter((r) => !r.customer_id);

  const [customer, setCustomer] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [due, setDue] = useState('');
  const [notes, setNotes] = useState('');
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  // THE FORM IS SEEDED ONCE PER OPENING, never again while it is open (a
  // refetch threw away what the reader had picked). A rebill seeds from a
  // FRESH runs read: the void that frees its runs has just invalidated the
  // list, and the cached one still has them on the live invoice.
  const presetKey = presetRunIds.join(',');
  const opened = useOpenCount(open);
  const [seededFor, setSeededFor] = useState(0);
  const runsFresh = !!runsQuery.data && !runsQuery.isFetching;
  const waitingForPreset = presetRunIds.length > 0 && seededFor !== opened;
  React.useEffect(() => {
    if (!open || opened === 0 || seededFor === opened) return;
    if (presetRunIds.length > 0 && !runsFresh) return;
    const pre = billable.filter((r) => presetRunIds.includes(r.id));
    const cust = pre[0]?.customer_id ?? '';
    setCustomer(cust);
    setPicked(pre.filter((r) => r.customer_id === cust && isInvoiceable(r)).map((r) => r.id));
    setDue('');
    setNotes('');
    setRefusal(null);
    setSeededFor(opened);
    // presetRunIds is compared by its ids (presetKey); billable is read as it is when seeding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opened, seededFor, runsFresh, presetKey]);

  const runs = billable
    .filter((r) => r.customer_id === customer)
    .sort((a, b) => Number(isInvoiceable(b)) - Number(isInvoiceable(a)) || (a.assembly_number ?? '').localeCompare(b.assembly_number ?? '') || (a.run_number ?? 0) - (b.run_number ?? 0));
  const ready = runs.filter(isInvoiceable);
  const selected = ready.filter((r) => picked.includes(r.id));
  const currencyOf = useMemo(() => new Map((ticketsQuery.data?.data ?? []).map((t) => [t.id, t.currency])), [ticketsQuery.data]);
  const currencies = new Set(selected.map((r) => (r.dh_ticket_id ? currencyOf.get(r.dh_ticket_id) : null)).filter(Boolean));
  const jurisdictions = new Set(selected.map((r) => r.province_state ?? 'not set'));
  const tickets = new Set(selected.map((r) => r.ticket_number).filter(Boolean));
  const blocker =
    selected.length === 0
      ? ready.length === 0 && customer
        ? "None of this customer's runs has an approved ticket yet."
        : 'Pick at least one run with an approved ticket.'
      : currencies.size > 1
        ? 'These runs were approved in more than one currency. Invoice each currency on its own.'
        : jurisdictions.size > 1
          ? `These runs are in different provinces or states (${Array.from(jurisdictions).join(', ')}). Invoice each on its own.`
          : null;

  const toggle = (id: string) => setPicked((all) => (all.includes(id) ? all.filter((x) => x !== id) : [...all, id]));

  const submit = async () => {
    if (blocker) return;
    if (due && !DAY.test(due)) {
      toast.error('The due date must be a date.');
      return;
    }
    if (!onlineOrSay(toast.error)) return;
    setRefusal(null);
    try {
      const res = await generate.mutateAsync({ run_ids: selected.map((r) => r.id), ...(due && { due_date: due }), ...(notes.trim() && { notes: notes.trim() }) });
      toast.success(`${res.data?.invoice_number ?? 'Invoice'} created as a draft`);
      onClose();
      if (res.data?.id) navigate(`/downhole/invoices/${res.data.id}`);
    } catch (error) {
      // The API's sentence as written; for the ticket gate, its runs[] too.
      const data = (error as { response?: { data?: { runs?: unknown } } })?.response?.data;
      const list = apiErrorCode(error) === 'RUN_NOT_CUSTOMER_APPROVED' && Array.isArray(data?.runs) ? (data!.runs as Refusal['runs']) : [];
      setRefusal({ message: getApiErrorMessage(error, 'The invoice was not created.'), runs: list });
    }
  };

  return (
    <DhDialog
      open={open}
      wide
      title={title}
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto min-w-0 basis-full self-center text-xs text-fieldiq-muted sm:basis-auto sm:flex-1">
            {blocker ??
              `${selected.length} ${selected.length === 1 ? 'run' : 'runs'}, billed at the prices approved on ${Array.from(tickets).join(', ') || 'their tickets'} — a draft invoice is created; nothing is sent.`}
          </span>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={!!blocker || generate.isPending} onClick={submit}>
            {generate.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Create draft invoice
          </button>
        </>
      }
    >
      {runsQuery.isLoading || (!runsFailure && !runsQuery.data) || (waitingForPreset && !(runsFailure && !runsQuery.isFetching)) ? (
        <div className="flex justify-center py-10">
          <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading runs" />
        </div>
      ) : runsFailure && !runsQuery.data ? (
        <LoadFailureNotice error={queryFailureError(runsQuery)} kind={runsFailure} subject="runs" />
      ) : (
        <div className="space-y-4">
          {refusal && (
            <div role="alert" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
              <p className="font-bold">{refusal.message}</p>
              {refusal.runs.length > 0 && (
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {refusal.runs.map((r) => {
                    const run = billable.find((x) => x.id === r.run_id);
                    return (
                      <li key={r.run_id}>
                        Run {r.run_number ?? '—'}
                        {r.ticket_number ? (
                          <>
                            {' '}— ticket{' '}
                            {run?.dh_ticket_id ? (
                              <Link to={`/downhole/tickets/${run.dh_ticket_id}`} className="font-bold text-fieldiq-accent-signal underline" onClick={onClose}>
                                {r.ticket_number}
                              </Link>
                            ) : (
                              r.ticket_number
                            )}
                          </>
                        ) : (
                          ' — no ticket'
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {customers.length === 0 ? (
            <p className="text-sm text-fieldiq-text">
              No run is ready to bill. A run can be billed once it is closed (or recorded as mobilized, not run), the customer has approved its
              field ticket, and it is not on an invoice.
            </p>
          ) : (
            <DhField label="Customer *">
              <select
                className={DH_INPUT}
                value={customer}
                onChange={(e) => {
                  setCustomer(e.target.value);
                  setPicked([]);
                }}
              >
                <option value="">Choose…</option>
                {customers.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </DhField>
          )}
          {noCustomer.length > 0 && (
            <p className="text-xs text-fieldiq-muted">
              {noCustomer.length} finished {noCustomer.length === 1 ? 'run has' : 'runs have'} no customer and cannot be invoiced until the
              assembly or run names one.
            </p>
          )}

          {customer && (
            <fieldset>
              <legend className="mb-2 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Runs to bill</legend>
              <ul className="divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
                {runs.map((r) => {
                  // ONE rule decides whether a run can be picked (isInvoiceable);
                  // ticketGate only words why not and where to go.
                  const can = isInvoiceable(r);
                  const gate = can ? null : ticketGate(r);
                  const on = picked.includes(r.id);
                  return (
                    <li key={r.id} className="px-3 py-3 text-sm" data-testid={`invoice-run-${r.id}`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 disabled:cursor-not-allowed"
                          checked={on && can}
                          disabled={!can}
                          onChange={() => toggle(r.id)}
                          aria-label={`Bill ${r.assembly_number} run ${r.run_number}`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-mono font-extrabold text-fieldiq-accent-signal">
                              {r.assembly_number ?? '—'} · run {r.run_number ?? '—'}
                            </span>
                            {r.dh_ticket_id ? (
                              <TicketStatusBadge status={r.ticket_status} />
                            ) : (
                              <span className="rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">
                                No ticket
                              </span>
                            )}
                          </span>
                          <span className="block text-xs text-fieldiq-muted">
                            {r.run_state === 'not_run' ? 'Mobilized, not run' : `In ${formatInstantDay(r.run_in_at, tz) ?? '—'}`} ·{' '}
                            {dhLabelOrText(DH_BILLING_TYPE_LABEL, r.billing_type)} · {r.province_state ?? 'province/state not set'}
                            {r.ticket_number ? ` · ticket ${r.ticket_number}` : ''}
                          </span>
                          <span className="block text-xs text-fieldiq-text">{[r.well_name, r.rig_name].filter(Boolean).join(' · ') || '—'}</span>
                          {gate && (
                            <span className="mt-1 block text-xs text-fieldiq-text">
                              Not billable yet: {gate.text}.{' '}
                              <Link to={gate.to} className="font-bold text-fieldiq-accent-signal underline" onClick={onClose}>
                                {gate.link}
                              </Link>
                            </span>
                          )}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          )}

          {customer && (
            <div className="grid gap-4 sm:grid-cols-2">
              <DhField label="Due date" hint="Leave blank and the payment terms set it — the customer's, else the company's (Net 30 if neither has any).">
                <input type="date" className={DH_DATE_INPUT} value={due} onChange={(e) => setDue(e.target.value)} />
              </DhField>
              <DhField label="Notes">
                <input className={DH_INPUT} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={10000} />
              </DhField>
            </div>
          )}
        </div>
      )}
    </DhDialog>
  );
}
